import { createSelector, createSlice } from "@reduxjs/toolkit";

import {
    getDesignations,
    getDirections,
    getLeagues,
    getYears,
} from "./leagueManagementActions";

import { authActions } from "../auth/authSlice";

import { RootState } from "../../store";
import {
    IAgeLevel,
    IDesignation,
    ILeague,
    ILeagueDirection,
    TLeagueSeasons,
    TLeagueYears,
} from "../../types/api";
import { IStateDefaults } from "../../types/shared";

export interface LeagueManagementState extends IStateDefaults {
    leagues: ILeague[];
    directions: ILeagueDirection[];
    designations: IDesignation[];
    selectedLeague?: ILeague["id"];
    years?: {
        [year in TLeagueYears]: {
            [season in TLeagueSeasons]: IAgeLevel["ageLevel"][];
        };
    };
}

const initialState: LeagueManagementState = {
    leagues: [],
    directions: [],
    designations: [],
    selectedLeague: undefined,
    loading: false,
    success: false,
    years: undefined,
};

const leagueManagementSlice = createSlice({
    name: "clubhouse",
    initialState,
    reducers: {
        setSelectedLeague: (state, action) => {
            state.selectedLeague = action.payload;
        },
    },
    extraReducers: builder => {
        builder
            .addCase(authActions.logout, state => {
                state.leagues = [];
                state.selectedLeague = undefined;
            })
            .addCase(getLeagues.pending, state => {
                state.loading = true;
            })
            .addCase(getLeagues.fulfilled, (state, { payload }) => {
                state.leagues = payload.leagues;
                state.loading = false;
            })
            .addCase(getLeagues.rejected, state => {
                state.loading = false;
            })
            .addCase(getYears.pending, state => {
                state.loading = true;
            })
            .addCase(getYears.fulfilled, (state, { payload }) => {
                state.years = payload.years;
                state.loading = false;
            })
            .addCase(getYears.rejected, state => {
                state.loading = false;
            })
            .addCase(getDesignations.pending, state => {
                state.loading = true;
            })
            .addCase(getDesignations.fulfilled, (state, { payload }) => {
                state.designations = payload.designations;
                state.loading = false;
            })
            .addCase(getDesignations.rejected, state => {
                state.loading = false;
            })
            .addCase(getDirections.pending, state => {
                state.loading = true;
            })
            .addCase(getDirections.fulfilled, (state, { payload }) => {
                state.directions = payload.directions;
                state.loading = false;
            })
            .addCase(getDirections.rejected, state => {
                state.loading = false;
            });
    },
});

export const leagueManagementActions = {
    ...leagueManagementSlice.actions,
    getLeagues: getLeagues,
    getYears: getYears,
    getDesignations: getDesignations,
    getDirections: getDirections,
};

export default leagueManagementSlice.reducer;

export const selectSelectedLeague = createSelector(
    [(state: RootState) => state.leaguemanagement],
    leaguemanagement => {
        const selectedLeague = leaguemanagement.selectedLeague;
        if (selectedLeague) {
            const league = leaguemanagement.leagues.find(
                league => league.id === selectedLeague,
            );
            return league;
        }
        return undefined;
    },
);

export const selectYears = createSelector(
    [(state: RootState) => state.leaguemanagement],
    leaguemanagement => leaguemanagement.years,
);

export const selectLeagues = createSelector(
    [(state: RootState) => state.leaguemanagement],
    leaguemanagement => leaguemanagement.leagues,
);

export const selectDesignations = createSelector(
    [(state: RootState) => state.leaguemanagement],
    leaguemanagement => leaguemanagement.designations,
);

export const selectDirections = createSelector(
    [(state: RootState) => state.leaguemanagement],
    leaguemanagement => leaguemanagement.directions,
);
